import { Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useT } from "@/glossary";
import {
  EXERCISES,
  type ExerciseId,
  useEverythingFinishedOnce,
  useIsFirstAllDone,
} from "@/lib/exercises";
import { formatScore, getScoreFeedback } from "@/lib/score";

type NextStep = {
  prompt?: string;
  label: string;
  to: (typeof EXERCISES)[ExerciseId]["path"];
};

/**
 * Results page of an exercise.
 * - `nextStep`: shown instead of best-score details until every exercise was finished once.
 * - `againLabel`: button to redo the exercise (next to Home) once everything was finished.
 */
export const ExerciseResults = ({
  id,
  nextStep,
  againLabel,
}: {
  id: ExerciseId;
  nextStep?: NextStep;
  againLabel?: string;
}) => {
  const t = useT();
  const navigate = useNavigate();
  const { store, path } = EXERCISES[id];
  const summaryData = store.useSummaryData();
  const state = store.useGlobalState();
  const everythingFinishedOnce = useEverythingFinishedOnce();
  const isFirstAllDone = useIsFirstAllDone(id);

  if (!summaryData) return null;
  if (isFirstAllDone) return <Navigate to="/summary" />;

  const currentSession = summaryData.sessions[summaryData.sessions.length - 1];
  const score = currentSession?.score ?? 0;
  const bestScore = state?.bestScore ?? score;
  const isBestScore = score >= bestScore;

  if (nextStep && !everythingFinishedOnce) {
    return (
      <div className="flex flex-col items-center gap-6 p-8">
        <h1 className="font-bold text-2xl">{t.common.exerciseComplete}</h1>
        <p>{getScoreFeedback(score, summaryData.maxScore, t)}</p>
        {nextStep.prompt && <p className="text-center">{nextStep.prompt}</p>}
        <Link
          to={nextStep.to}
          className="rounded border border-foreground px-4 py-2 text-center"
        >
          {nextStep.label}
        </Link>
      </div>
    );
  }

  const homeLink = (
    <Link
      to="/"
      className="rounded border border-foreground px-4 py-2 text-center"
    >
      {t.common.home}
    </Link>
  );

  return (
    <div className="flex flex-col items-center gap-6 p-8">
      <h1 className="font-bold text-2xl">{t.common.exerciseComplete}</h1>
      <div className="text-center">
        <p>{getScoreFeedback(score, summaryData.maxScore, t)}</p>
        {score < bestScore && (
          <p className="text-sm">
            {t.common.bestScore(
              formatScore(bestScore),
              formatScore(summaryData.maxScore),
            )}
          </p>
        )}
        {isBestScore && score > 0 && (
          <p className="mt-2 font-bold text-brand">{t.common.newBestScore}</p>
        )}
      </div>
      {againLabel ? (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: path })}
            className="rounded border border-foreground px-4 py-2"
          >
            {againLabel}
          </button>
          {homeLink}
        </div>
      ) : (
        homeLink
      )}
    </div>
  );
};
